import {useVariableSuggestions} from 'hooks/useVariableSuggestions'
import {
 LanguageItem,
 TemplateRow,
} from 'models/CreatePromotion'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {fetchLanguageList} from 'utils/api/auth/productPromotionService'
type FormState = {
 lang: LanguageItem | null
 template_name: string
 template_text: string
}

const VARIABLE_OPTIONS = [
 'price',
 'aggregator_centre_name',
 'aggregator_phone_number',
]

const TemplateDialog = ({
 onClose,
 onSubmit,
 templates,
 editData,
}: {
 onClose: () => void
 onSubmit: (data: Omit<TemplateRow, 'id'>) => void
 templates: TemplateRow[]
 editData?: TemplateRow | null
}) => {
 const {t} = useTranslation()
 const [form, setForm] = useState<FormState>({
  lang: editData?.lang || null,
  template_name: editData?.template_name || '',
  template_text: editData?.template_text || '',
 })

 const [open, setOpen] = useState(false)
 const [languages, setLanguages] = useState<LanguageItem[]>(
  []
 )

 const isValid =
  !!form.lang &&
  form.template_name.trim() &&
  form.template_text.trim()

 const {
  textareaRef,
  debounceRef,
  showSuggestions,
  filteredOptions,
  dropdownPos,
  handleTextChange,
  applySuggestion,
  setShowSuggestions,
  handleKeyDown,
  activeIndex,
  setActiveIndex,
 } = useVariableSuggestions({
  options: VARIABLE_OPTIONS,
 })

 useEffect(() => {
  const loadLanguages = async () => {
   const res = await fetchLanguageList()
   setLanguages(res)
   const usedLanguageCodes = new Set(
    templates
     .map((t) => t.lang?.language_code)
     .filter(Boolean)
   )

   const filtered = res.filter(
    (lang) => !usedLanguageCodes.has(lang.language_code)
   )

   setLanguages(filtered)
  }
  void loadLanguages()
 }, [])

 useEffect(() => {
  return () => {
   if (debounceRef.current) {
    clearTimeout(debounceRef.current)
   }
  }
 }, [])

 return (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
   <div className='relative w-[520px] rounded-2xl bg-white p-6'>
    <div
     onClick={onClose}
     className='absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full hover:bg-[#F3F4F6]'
    >
     ✕
    </div>

    <h2 className='mb-6 text-lg font-semibold'>
     {t('templateDialog.title')}
    </h2>

    <div className='space-y-5'>
     <div
      className={`rounded-xl border px-4 py-3 relative ${
       form.lang && form.lang.language
        ? 'bg-[#F9FAFB]'
        : 'bg-white'
      }`}
     >
      <p className='text-xs font-medium text-[#3F76FF] mb-1'>
       {t('templateDialog.language')}
       <span className='text-red-500'>*</span>
      </p>

      <button
       type='button'
       onClick={() => !form.lang && setOpen(!open)}
       className='flex w-full items-center justify-between bg-transparent text-left outline-none'
      >
       <span
        className={
         form.lang && form.lang.language
          ? 'text-gray-900'
          : 'text-gray-400'
        }
       >
        {form.lang?.language ||
         t('templateDialog.selectLanguage')}
       </span>

       <svg
        className={`h-4 w-4 transition-transform ${
         open ? 'rotate-180' : ''
        }`}
        viewBox='0 0 20 20'
        fill='none'
        stroke='currentColor'
       >
        <path d='M6 8l4 4 4-4' strokeWidth='2' />
       </svg>
      </button>

      {open && (
       <div className='absolute left-0 top-full z-10 mt-2 w-full rounded-xl bg-[#F5F8FA] border border-[#E6EBF0] shadow-lg'>
        {languages.map((lang, index) => (
         <div
          key={lang.language_code}
          onMouseDown={() => {
           setForm({...form, lang: lang})
           setOpen(false)
          }}
          className={`flex cursor-pointer items-center gap-3 px-4 py-4 hover:bg-gray-100 ${
           index !== languages.length - 1
            ? 'border-b border-[#E6EBF0]'
            : ''
          }`}
         >
          <span
           className={`flex h-5 w-5 items-center justify-center rounded-full border-2 
             ${
              form.lang?.language_code ===
              lang.language_code
               ? 'border-blue-500'
               : 'border-gray-300'
             }
           `}
          >
           {form.lang?.language_code ===
            lang.language_code && (
            <span className='h-2.5 w-2.5 rounded-full bg-blue-500' />
           )}
          </span>

          <span>{lang.language}</span>
         </div>
        ))}
       </div>
      )}
     </div>
     <div
      className={`rounded-xl border px-4 py-3 ${
       form.template_name ? 'bg-[#F9FAFB]' : 'bg-white'
      }`}
     >
      <p className='text-xs font-medium text-[#3F76FF] mb-1'>
       {t('templateDialog.templateName')}
       <span className='text-red-500'>*</span>
      </p>
      <input
       value={form.template_name}
       onChange={(e) =>
        setForm({...form, template_name: e.target.value})
       }
       placeholder={t('templateDialog.enterTemplateName')}
       className='w-full bg-transparent outline-none text-gray-900 placeholder-gray-400'
      />
     </div>
     <div className='relative'>
      <div
       className={`rounded-xl border px-4 py-3 ${
        form.template_text ? 'bg-[#F9FAFB]' : 'bg-white'
       }`}
      >
       <p className='text-xs font-medium text-[#3F76FF] mb-1'>
        {t('templateDialog.templateText')}
        <span className='text-red-500'>*</span>
       </p>

       <textarea
        ref={textareaRef}
        rows={4}
        maxLength={200}
        value={form.template_text}
        onChange={(e) =>
         handleTextChange(
          e.target.value,
          e.target.selectionStart || 0,
          (val) =>
           setForm((prev) => ({
            ...prev,
            template_text: val,
           }))
         )
        }
        onKeyDown={(e) =>
         handleKeyDown(e, form.template_text, (val) =>
          setForm({...form, template_text: val})
         )
        }
        onBlur={(e) => {
         if (e.relatedTarget === null) {
          setTimeout(() => setShowSuggestions(false), 150)
         }
        }}
        placeholder={t('templateDialog.enterTemplateText')}
        className='w-full resize-none bg-transparent outline-none'
       />
      </div>

      {showSuggestions && filteredOptions.length > 0 && (
       <div
        style={{
         position: 'absolute',
         top: dropdownPos.top,
         left: dropdownPos.left,
        }}
        className='z-50'
       >
        <div className='bg-white border border-[#E6EBF0] rounded-xl shadow-lg overflow-hidden min-w-[220px]'>
         {filteredOptions.map((option, index) => (
          <div
           key={option}
           onMouseEnter={() => setActiveIndex(index)}
           onMouseDown={() => {
            const newText = applySuggestion(
             form.template_text,
             option
            )

            setForm({...form, template_text: newText})
           }}
           className={`px-4 py-2 cursor-pointer ${
            index === activeIndex ? 'bg-[#F5F8FA]' : ''
           }`}
          >
           {option}
          </div>
         ))}
        </div>
       </div>
      )}
     </div>
    </div>
    <button
     onClick={() => {
      if (!isValid) return
      onSubmit({
       ...form,
       lang: form.lang ?? undefined,
      })
      onClose()
     }}
     className={`mt-8 w-full rounded-xl py-3 text-sm font-medium ${
      isValid
       ? 'bg-[#5E7CE2] text-white'
       : 'bg-[#F1F1F1] text-[#9CA3AF] cursor-not-allowed'
     }`}
    >
     {t('templateDialog.saveTemplate')}
    </button>
   </div>
  </div>
 )
}

export default TemplateDialog
